// frontend/src/components/arena/DuelStreamPanel.jsx
// The watch page of a streamed duel (rules.stream, backend arena_play.py /api/arena/stream/:code): both players'
// charts with their trades. The server only sends bars both players have already seen and trades 2 minutes late
// (none at all until the end when the host hid them), so nothing here can help either player. Polls every 10 s.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { duelFetch } from '../../pages/duelIdentity';
import DuelCandles, { DUEL_COLORS } from './DuelCandles';
import DuelReactions from '../../pages/arena/DuelReactions';

const T = { sunken: '#0c0e11', line: 'rgba(255,255,255,.08)', ink3: '#8b929b', up: '#3dd68c', down: '#ff6a60' };

export default function DuelStreamPanel() {
  const { inviteCode } = useParams();
  const [s, setS] = useState(null);
  const [idx, setIdx] = useState(null);
  useEffect(() => {
    let off = false;
    const load = () => duelFetch(`/api/arena/stream/${encodeURIComponent(inviteCode)}`).then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!off && j) setS(j); }).catch(() => {});
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 10000);
    return () => { off = true; clearInterval(t); };
  }, [inviteCode]);
  if (!s || !s.streaming) return null;
  const charts = s.charts || [];
  const i = idx == null ? charts.length - 1 : Math.min(idx, charts.length - 1);
  const chart = charts[i];
  return (
    <Box component="section" aria-label="Live stream" sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3, color: '#f2f4f6', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.status === 'active' ? '#ff6a60' : '#8b929b' }} />
        <Box component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700 }}>{s.status === 'active' ? 'Live stream' : s.status === 'complete' ? 'Full replay' : 'Stream starts with the duel'}</Box>
        <Box sx={{ flex: 1 }} />
        {s.names && Object.entries(DUEL_COLORS).map(([role, c]) => (
          <Box key={role} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />{s.names[role]}
            {s.balances?.[role] != null && <Box component="span" sx={{ color: '#8b929b', fontVariantNumeric: 'tabular-nums' }}>${Math.round(Number(s.balances[role])).toLocaleString('en-US')}</Box>}
          </Box>
        ))}
      </Box>
      {s.status === 'active' && (
        <Box sx={{ fontSize: 12.5, color: '#8b929b' }}>
          Bars appear once both players have seen them{s.show_trades ? `, and trades ${Math.round(s.delay_s / 60)} minutes late` : '. Trades stay hidden until the duel ends'}.
        </Box>
      )}
      {charts.length > 1 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {charts.map((c, k) => (
            <Box key={k} component="button" type="button" onClick={() => setIdx(k)} aria-pressed={k === i} sx={{
              border: '1px solid rgba(255,255,255,.14)', borderRadius: 999, px: 1.5, py: 0.5, font: 'inherit', fontSize: 12.5, cursor: 'pointer',
              bgcolor: k === i ? '#f2f4f6' : 'transparent', color: k === i ? '#0d1116' : '#f2f4f6',
            }}>Chart {k + 1} · {c.symbol}</Box>
          ))}
        </Box>
      )}
      <Box sx={{ position: 'relative' }}>
        {chart && chart.bars.length > 0 ? <DuelCandles chart={chart} T={T} /> : <Box sx={{ fontSize: 13, color: '#8b929b', minHeight: 120 }}>No bars to show yet.</Box>}
        {s.duel_id && <DuelReactions duelId={s.duel_id} canSend={s.status === 'active'} />}
      </Box>
    </Box>
  );
}
